<template>
  <Teleport to="body">
    <transition name="archived-leads-modal">
      <div
        v-if="open"
        class="alm-backdrop"
        role="dialog"
        aria-modal="true"
        :aria-label="t('kanban.page.archivedLeadsModal.title')"
        @click.self="emit('close')"
      >
        <div class="alm-panel">
          <header class="alm-header">
            <div>
              <h2 class="alm-title">{{ t('kanban.page.archivedLeadsModal.title') }}</h2>
              <p class="alm-subtitle">{{ t('kanban.page.archivedLeadsModal.subtitle') }}</p>
            </div>
            <button type="button" class="alm-close" :aria-label="t('kanban.page.archivedLeadsModal.close')" @click="emit('close')">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div class="alm-toolbar">
            <div class="alm-search-wrap">
              <svg class="alm-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              <input
                v-model="searchInput"
                type="search"
                class="alm-search"
                :placeholder="t('kanban.page.archivedLeadsModal.searchPlaceholder')"
                @keydown.enter.prevent="aplicarBusca"
              />
            </div>
            <button type="button" class="alm-search-btn" @click="aplicarBusca">
              <svg class="alm-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              <span>{{ t('kanban.page.archivedLeadsModal.search') }}</span>
            </button>
          </div>

          <div class="alm-body">
            <div v-if="loading" class="alm-state">{{ t('kanban.page.archivedLeadsModal.loading') }}</div>
            <div v-else-if="!items.length" class="alm-state">{{ t('kanban.page.archivedLeadsModal.empty') }}</div>
            <div v-else class="alm-table-wrap">
              <table class="alm-table">
                <thead>
                  <tr>
                    <th>{{ t('kanban.page.archivedLeadsModal.colName') }}</th>
                    <th>{{ t('kanban.page.archivedLeadsModal.colWhatsapp') }}</th>
                    <th>{{ t('kanban.page.archivedLeadsModal.colEmail') }}</th>
                    <th>{{ t('kanban.page.archivedLeadsModal.colDate') }}</th>
                    <th class="alm-col-actions">{{ t('kanban.page.archivedLeadsModal.colActions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="lead in items"
                    :key="lead.id"
                    class="alm-row"
                    @click="emit('open-lead', lead)"
                  >
                    <td class="alm-name">{{ lead.nome || '—' }}</td>
                    <td>{{ formatPhone(lead.telefone) }}</td>
                    <td>{{ lead.email || '—' }}</td>
                    <td>{{ formatDate(lead.dataCriacao || lead.dataUpdate) }}</td>
                    <td class="alm-col-actions" @click.stop>
                      <button
                        type="button"
                        class="alm-unarchive"
                        :disabled="busyId === lead.id"
                        @click="emit('unarchive', lead)"
                      >
                        <svg
                          v-if="busyId !== lead.id"
                          class="alm-btn-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        <span>{{ busyId === lead.id ? t('kanban.page.archivedLeadsModal.pleaseWait') : t('kanban.page.archivedLeadsModal.unarchive') }}</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <footer v-if="totalPages > 1" class="alm-footer">
            <span>{{ rangeLabel }}</span>
            <div class="alm-pager">
              <button type="button" :disabled="loading || page <= 0" @click="emit('page-change', page - 1)">‹</button>
              <span>{{ page + 1 }} / {{ totalPages }}</span>
              <button type="button" :disabled="loading || page >= totalPages - 1" @click="emit('page-change', page + 1)">›</button>
            </div>
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatBrazilPhoneForDisplay } from '@/utils/phoneBr'

const { t } = useI18n()

const props = defineProps({
  open: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  items: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
  page: { type: Number, default: 0 },
  pageSize: { type: Number, default: 30 },
  totalPages: { type: Number, default: 0 },
  busyId: { type: [Number, String], default: null },
  search: { type: String, default: '' },
})

const emit = defineEmits(['close', 'open-lead', 'unarchive', 'page-change', 'search'])

const searchInput = ref(props.search || '')

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) searchInput.value = props.search || ''
  }
)

watch(
  () => props.search,
  (v) => {
    searchInput.value = v || ''
  }
)

const aplicarBusca = () => {
  emit('search', (searchInput.value || '').trim())
}

const rangeLabel = computed(() => {
  if (!props.total) return ''
  const from = props.page * props.pageSize + 1
  const to = Math.min((props.page + 1) * props.pageSize, props.total)
  return t('kanban.page.archivedLeadsModal.range', { from, to, total: props.total })
})

const formatPhone = (raw) => {
  if (!raw) return '—'
  try {
    return formatBrazilPhoneForDisplay(raw) || raw
  } catch {
    return raw
  }
}

const formatDate = (raw) => {
  if (raw == null || raw === '') return '—'
  try {
    let d = null
    if (Array.isArray(raw) && raw.length >= 3) {
      // Jackson LocalDateTime: [year, month, day, hour, minute, second, nano]
      const [y, mo, day, h = 0, mi = 0, s = 0] = raw
      d = new Date(y, mo - 1, day, h, mi, s)
    } else if (typeof raw === 'object' && raw.year != null && raw.monthValue != null) {
      d = new Date(
        raw.year,
        raw.monthValue - 1,
        raw.dayOfMonth || raw.day || 1,
        raw.hour || 0,
        raw.minute || 0,
        raw.second || 0
      )
    } else {
      const str = String(raw).trim()
      // "2026,7,24,12,11,0,562949000" (array toString)
      if (/^\d{4},\d{1,2},\d{1,2}/.test(str)) {
        const parts = str.split(',').map((p) => Number(p))
        d = new Date(parts[0], parts[1] - 1, parts[2], parts[3] || 0, parts[4] || 0, parts[5] || 0)
      } else {
        d = new Date(str)
      }
    }
    if (!d || Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return '—'
  }
}
</script>

<style scoped>
.alm-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.alm-panel {
  width: min(920px, 100%);
  max-height: min(85vh, 720px);
  background: #fff;
  color: #111827;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dark .alm-panel {
  background: #1f2937;
  color: #f9fafb;
}

.alm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.dark .alm-header {
  border-bottom-color: #374151;
}

.alm-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.dark .alm-title {
  color: #f9fafb;
}

.alm-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: #4b5563;
}

.dark .alm-subtitle {
  color: #9ca3af;
}

.alm-close {
  border: none;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
}

.alm-close:hover {
  background: #f3f4f6;
  color: #111827;
}

.dark .alm-close {
  color: #9ca3af;
}

.dark .alm-close:hover {
  background: #374151;
  color: #f9fafb;
}

.alm-toolbar {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.dark .alm-toolbar {
  border-bottom-color: #374151;
}

.alm-search-wrap {
  position: relative;
  flex: 1;
}

.alm-search-icon {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  color: #6b7280;
}

.alm-search {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  font-size: 0.875rem;
}

.alm-search::placeholder {
  color: #9ca3af;
}

.dark .alm-search {
  background: #111827;
  border-color: #4b5563;
  color: #f9fafb;
}

.dark .alm-search::placeholder {
  color: #6b7280;
}

.alm-search-btn,
.alm-unarchive {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.85rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  background: #2563eb;
  color: #fff;
  white-space: nowrap;
}

.alm-btn-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.alm-search-btn:hover,
.alm-unarchive:hover:not(:disabled) {
  background: #1d4ed8;
}

.alm-unarchive:disabled {
  opacity: 0.6;
  cursor: wait;
}

.alm-body {
  flex: 1;
  overflow: auto;
  padding: 0.5rem 0;
  color: #111827;
}

.dark .alm-body {
  color: #f9fafb;
}

.alm-state {
  padding: 2rem 1.25rem;
  text-align: center;
  color: #4b5563;
  font-size: 0.875rem;
}

.dark .alm-state {
  color: #9ca3af;
}

.alm-table-wrap {
  overflow-x: auto;
}

.alm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  color: #111827;
}

.dark .alm-table {
  color: #f9fafb;
}

.alm-table th {
  text-align: left;
  padding: 0.6rem 1rem;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.dark .alm-table th {
  color: #9ca3af;
  border-bottom-color: #374151;
}

.alm-table td {
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
  color: #111827;
}

.dark .alm-table td {
  border-bottom-color: #374151;
  color: #e5e7eb;
}

.alm-row {
  cursor: pointer;
}

.alm-row:hover {
  background: #f8fafc;
}

.dark .alm-row:hover {
  background: #111827;
}

.alm-name {
  font-weight: 500;
  color: #111827;
}

.dark .alm-name {
  color: #f9fafb;
}

.alm-col-actions {
  text-align: right;
  white-space: nowrap;
}

.alm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #e5e7eb;
  font-size: 0.8125rem;
  color: #4b5563;
}

.dark .alm-footer {
  border-top-color: #374151;
  color: #9ca3af;
}

.alm-pager {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.alm-pager button {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #111827;
  border-radius: 6px;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
}

.dark .alm-pager button {
  background: #111827;
  border-color: #4b5563;
  color: #f9fafb;
}

.alm-pager button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.archived-leads-modal-enter-active,
.archived-leads-modal-leave-active {
  transition: opacity 0.15s ease;
}

.archived-leads-modal-enter-from,
.archived-leads-modal-leave-to {
  opacity: 0;
}
</style>
